import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MdBadge,
  MdCheckCircle,
  MdPersonAdd,
  MdRefresh,
  MdSearch,
  MdWarningAmber,
} from "react-icons/md";
import { toast } from "react-toastify";
import Card from "../../../components/card";
import api from "../../../services/axios";
import { mensagemDoServidor } from "../../../utils/mensagemDoServidor";
import ConfirmarPromoverModal from "./components/ConfirmarPromoverModal";

/*
  As pessoas do tenant que ainda NÃO são colaboradores de verdade.

  ⚠ Esta tela existe porque elas são invisíveis em todo o resto do portal: a lista de colaboradores
  só monta a linha quando encontra um LOGIN com aquele e-mail, e pessoa de diretório não tem login.
  Eram 67 das 75 pessoas ativas quando isto foi escrito — ou seja, a maior parte da empresa estava
  no banco e em lugar nenhum da tela.

  Promover é dizer "esta pessoa trabalha aqui", e só isso. Não cria login e não concede perfil: o
  que a pessoa alcança é decisão separada, na tela de Perfis. Juntar as duas coisas faria um clique
  dar acesso sem ninguém ter escolhido qual.
*/

interface PessoaDoDiretorio {
  id: number;
  name?: string | null;
  email?: string | null;
  hasEntraAccount: boolean;
  hasLogin: boolean;
  dtCreated?: string | null;
}

const formatarData = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR", { dateStyle: "short" }) : "—";

const DirectoryPage: React.FC = () => {
  const [pessoas, setPessoas] = useState<PessoaDoDiretorio[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState("");
  const [promovendo, setPromovendo] = useState<number | null>(null);
  const [aConfirmar, setAConfirmar] = useState<PessoaDoDiretorio | null>(null);
  const [promovidos, setPromovidos] = useState<PessoaDoDiretorio[]>([]);
  const [devolvendo, setDevolvendo] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    // A seção de promovidos é complemento: não derruba a lista principal. Só o 404 (backend antigo) some
    // calado; outra falha avisa, porque é por ela que se desfaz uma promoção errada.
    const [diretorio, promov] = await Promise.allSettled([
      api.get<PessoaDoDiretorio[]>("/Employee/directory"),
      api.get<PessoaDoDiretorio[]>("/Employee/directory/promovidos"),
    ]);
    if (diretorio.status === "fulfilled") setPessoas(diretorio.value.data);
    else toast.error("Não foi possível carregar as pessoas do diretório.");
    setPromovidos(promov.status === "fulfilled" ? promov.value.data : []);
    if (promov.status === "rejected" && (promov.reason as any)?.response?.status !== 404)
      toast.error("Não foi possível carregar os promovidos que ainda não entraram.");
    setCarregando(false);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return pessoas;
    return pessoas.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(termo) ||
        (p.email || "").toLowerCase().includes(termo)
    );
  }, [pessoas, busca]);

  const promover = async (pessoa: PessoaDoDiretorio) => {
    setPromovendo(pessoa.id);
    try {
      await api.post(`/Employee/directory/${pessoa.id}/promote`);
      setAConfirmar(null);
      toast.success(
        `${pessoa.name || pessoa.email} agora é colaborador. Defina o acesso em Perfis.`
      );
      // Recarrega as duas: quem acabou de ser promovido (sem login) entra em "promovidos".
      await carregar();
    } catch (e) {
      toast.error(mensagemDoServidor(e, "Não foi possível promover."));
    } finally {
      setPromovendo(null);
    }
  };

  const voltarAoDiretorio = async (pessoa: PessoaDoDiretorio) => {
    setDevolvendo(pessoa.id);
    try {
      await api.post(`/Employee/directory/${pessoa.id}/demote`);
      toast.success(`${pessoa.name || pessoa.email} voltou ao diretório.`);
      await carregar();
    } catch (e) {
      toast.error(mensagemDoServidor(e, "Não foi possível devolver ao diretório."));
    } finally {
      setDevolvendo(null);
    }
  };

  const semConta = pessoas.filter((p) => !p.hasEntraAccount).length;

  return (
    <div className="mt-3">
      <Card extra="w-full p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-bold text-navy-700 dark:text-white">
              <MdBadge className="h-6 w-6" />
              Pessoas do diretório
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-300">
              Quem existe no tenant da Microsoft e ainda não é colaborador no NINA. Elas já
              conseguem ver os comunicados no widget; promover libera aparecer em equipe,
              capacidade e apontamento de horas.
            </p>
          </div>

          <button
            type="button"
            onClick={carregar}
            className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
          >
            <MdRefresh className="h-4 w-4" />
            Atualizar
          </button>
        </div>

        {semConta > 0 && (
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
            <MdWarningAmber className="mt-0.5 h-5 w-5 shrink-0" />
            <span>
              {semConta === 1 ? "1 pessoa não tem" : `${semConta} pessoas não têm`} conta da
              Microsoft vinculada. Promover não basta — sem conta, elas não conseguem entrar.
            </span>
          </div>
        )}

        <div className="relative mt-5">
          <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome ou e-mail"
            className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-3 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-navy-900 dark:text-white"
          />
        </div>

        <div className="mt-5 overflow-x-auto">
          {carregando ? (
            <p className="py-8 text-center text-sm text-gray-500">Carregando…</p>
          ) : filtradas.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {pessoas.length === 0
                ? "Ninguém pendente — todo mundo do tenant já é colaborador."
                : "Nada encontrado para esta busca."}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-white/10">
                  <th className="py-2 pr-4 font-semibold">Nome</th>
                  <th className="py-2 pr-4 font-semibold">E-mail</th>
                  <th className="py-2 pr-4 font-semibold">Conta Microsoft</th>
                  <th className="py-2 pr-4 font-semibold">Visto em</th>
                  <th className="py-2 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {filtradas.map((pessoa) => (
                  <tr
                    key={pessoa.id}
                    className="border-b border-gray-100 last:border-0 dark:border-white/5"
                  >
                    <td className="py-3 pr-4 font-medium text-navy-700 dark:text-white">
                      {pessoa.name || "—"}
                    </td>
                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                      {pessoa.email || "—"}
                    </td>
                    <td className="py-3 pr-4">
                      {pessoa.hasEntraAccount ? (
                        <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                          <MdCheckCircle className="h-4 w-4" />
                          vinculada
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                          <MdWarningAmber className="h-4 w-4" />
                          sem conta
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-gray-500">{formatarData(pessoa.dtCreated)}</td>
                    <td className="py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setAConfirmar(pessoa)}
                        disabled={promovendo === pessoa.id}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <MdPersonAdd className="h-4 w-4" />
                        {promovendo === pessoa.id ? "Promovendo…" : "Promover"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {promovidos.length > 0 && (
        <Card extra="mt-4 w-full p-6">
          <h2 className="text-lg font-bold text-navy-700 dark:text-white">
            Promovidos que ainda não entraram
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-300">
            Já são colaboradores da DataValue, mas ainda não têm login. Se a promoção foi engano
            (por exemplo, a pessoa é de um cliente), devolva ao diretório antes que ela entre.
          </p>
          <ul className="mt-4 divide-y divide-gray-100 dark:divide-white/5">
            {promovidos.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                <div>
                  <div className="font-medium text-navy-700 dark:text-white">{p.name || "—"}</div>
                  <div className="text-gray-600 dark:text-gray-300">{p.email || "—"}</div>
                </div>
                <button
                  type="button"
                  onClick={() => voltarAoDiretorio(p)}
                  disabled={devolvendo === p.id}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
                >
                  {devolvendo === p.id ? "Devolvendo…" : "Voltar ao diretório"}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {aConfirmar && (
        <ConfirmarPromoverModal
          nome={aConfirmar.name || aConfirmar.email || "esta pessoa"}
          promovendo={promovendo === aConfirmar.id}
          onConfirmar={() => promover(aConfirmar)}
          onClose={() => setAConfirmar(null)}
        />
      )}
    </div>
  );
};

export default DirectoryPage;
